import type { Edge, Node, NodeGroup } from '../types';
import { getEdgeGeometry, sampleDisplayPolyline } from './edgeMath';
import { isStrokeMarkup, localArrowPoints } from './markup';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function pointInRect(x: number, y: number, r: Rect, pad = 0): boolean {
  return x >= r.x - pad && x <= r.x + r.w + pad && y >= r.y - pad && y <= r.y + r.h + pad;
}

export function segmentIntersectsSegment(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): boolean {
  const den = (bx - ax) * (dy - cy) - (by - ay) * (dx - cx);
  if (Math.abs(den) < 1e-8) return false;
  const t = ((cx - ax) * (dy - cy) - (cy - ay) * (dx - cx)) / den;
  const u = ((cx - ax) * (by - ay) - (cy - ay) * (bx - ax)) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

export function segmentIntersectsRect(
  x1: number, y1: number, x2: number, y2: number, r: Rect, pad = 0,
): boolean {
  const box: Rect = { x: r.x - pad, y: r.y - pad, w: r.w + pad * 2, h: r.h + pad * 2 };
  if (pointInRect(x1, y1, box) || pointInRect(x2, y2, box)) return true;
  const x3 = box.x + box.w;
  const y3 = box.y + box.h;
  return (
    segmentIntersectsSegment(x1, y1, x2, y2, box.x, box.y, x3, box.y)
    || segmentIntersectsSegment(x1, y1, x2, y2, x3, box.y, x3, y3)
    || segmentIntersectsSegment(x1, y1, x2, y2, x3, y3, box.x, y3)
    || segmentIntersectsSegment(x1, y1, x2, y2, box.x, y3, box.x, box.y)
  );
}

export function polylineHitsRect(pts: number[], r: Rect, pad = 10): boolean {
  for (let i = 0; i < pts.length - 2; i += 2) {
    if (segmentIntersectsRect(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], r, pad)) return true;
  }
  return false;
}

/** Ray-cast even-odd fill. `pts` is a flat [x,y,…] ring (auto-closed). */
export function pointInPolygon(x: number, y: number, pts: number[]): boolean {
  if (pts.length < 6) return false;
  let inside = false;
  const n = pts.length;
  for (let i = 0, j = n - 2; i < n; j = i, i += 2) {
    const xi = pts[i], yi = pts[i + 1];
    const xj = pts[j], yj = pts[j + 1];
    const hit = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi);
    if (hit) inside = !inside;
  }
  return inside;
}

export function polylineHitsPolygon(line: number[], poly: number[], pad = 10): boolean {
  for (let i = 0; i < line.length; i += 2) {
    if (pointInPolygon(line[i], line[i + 1], poly)) return true;
  }
  if (poly.length < 6) return false;
  const closed = poly[0] === poly[poly.length - 2] && poly[1] === poly[poly.length - 1]
    ? poly
    : [...poly, poly[0], poly[1]];
  for (let i = 0; i < line.length - 2; i += 2) {
    for (let j = 0; j < closed.length - 2; j += 2) {
      if (segmentIntersectsSegment(
        line[i], line[i + 1], line[i + 2], line[i + 3],
        closed[j], closed[j + 1], closed[j + 2], closed[j + 3],
      )) return true;
    }
  }
  if (pad <= 0) return false;
  // Treat a near-miss as a hit so a thin lasso still grabs the stroke.
  for (let i = 0; i < line.length; i += 2) {
    if (distanceToPolygon(line[i], line[i + 1], closed) <= pad) return true;
  }
  return false;
}

function distanceToPolygon(x: number, y: number, poly: number[]): number {
  let best = Infinity;
  for (let i = 0; i < poly.length - 2; i += 2) {
    const d = distToSegment(x, y, poly[i], poly[i + 1], poly[i + 2], poly[i + 3]);
    if (d < best) best = d;
  }
  return best;
}

function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

export function closePolygon(pts: number[]): number[] {
  if (pts.length < 6) return pts;
  if (pts[0] === pts[pts.length - 2] && pts[1] === pts[pts.length - 1]) return pts;
  return [...pts, pts[0], pts[1]];
}

export function polygonArea(pts: number[]): number {
  if (pts.length < 6) return 0;
  const closed = closePolygon(pts);
  let area = 0;
  for (let i = 0; i < closed.length - 2; i += 2) {
    area += closed[i] * closed[i + 3] - closed[i + 2] * closed[i + 1];
  }
  return Math.abs(area) / 2;
}

export function edgeDisplayPolyline(edge: Edge, source: Node, target: Node): number[] {
  const srcSock = source.sockets.find(s => s.id === edge.sourceSocketId);
  const tgtSock = target.sockets.find(s => s.id === edge.targetSocketId);
  if (!srcSock || !tgtSock) {
    return [
      source.position.x + source.size.width / 2,
      source.position.y + source.size.height / 2,
      target.position.x + target.size.width / 2,
      target.position.y + target.size.height / 2,
    ];
  }
  const geom = getEdgeGeometry(edge, source, target, srcSock, tgtSock);
  const bezier = !geom.useTension && geom.linePoints.length === 8;
  return sampleDisplayPolyline(geom.linePoints, bezier);
}

export function markupStrokePolyline(node: Node): number[] {
  const local = localArrowPoints(node);
  return [
    node.position.x + local.start.x,
    node.position.y + local.start.y,
    node.position.x + local.end.x,
    node.position.y + local.end.y,
  ];
}

function nodeCenter(node: { position: { x: number; y: number }; size: { width: number; height: number } }) {
  return {
    x: node.position.x + node.size.width / 2,
    y: node.position.y + node.size.height / 2,
  };
}

export function idsInRect(
  rect: Rect,
  nodes: Node[],
  edges: Edge[],
  groups: NodeGroup[],
): string[] {
  const ids: string[] = [];
  const nodeById = new Map(nodes.map(n => [n.id, n]));
  for (const n of nodes) {
    if (isStrokeMarkup(n.type)) {
      if (polylineHitsRect(markupStrokePolyline(n), rect, 12)) ids.push(n.id);
    } else {
      const c = nodeCenter(n);
      if (pointInRect(c.x, c.y, rect)) ids.push(n.id);
    }
  }
  for (const g of groups) {
    const c = nodeCenter(g);
    if (pointInRect(c.x, c.y, rect)) ids.push(g.id);
  }
  for (const e of edges) {
    const src = nodeById.get(e.sourceNodeId);
    const tgt = nodeById.get(e.targetNodeId);
    if (!src || !tgt) continue;
    if (polylineHitsRect(edgeDisplayPolyline(e, src, tgt), rect, 12)) ids.push(e.id);
  }
  return ids;
}

export function idsInPolygon(
  poly: number[],
  nodes: Node[],
  edges: Edge[],
  groups: NodeGroup[],
): string[] {
  const ring = closePolygon(poly);
  if (polygonArea(ring) < 64) return [];
  const ids: string[] = [];
  const nodeById = new Map(nodes.map(n => [n.id, n]));
  for (const n of nodes) {
    if (isStrokeMarkup(n.type)) {
      if (polylineHitsPolygon(markupStrokePolyline(n), ring, 12)) ids.push(n.id);
    } else {
      const c = nodeCenter(n);
      if (pointInPolygon(c.x, c.y, ring)) ids.push(n.id);
    }
  }
  for (const g of groups) {
    const c = nodeCenter(g);
    if (pointInPolygon(c.x, c.y, ring)) ids.push(g.id);
  }
  for (const e of edges) {
    const src = nodeById.get(e.sourceNodeId);
    const tgt = nodeById.get(e.targetNodeId);
    if (!src || !tgt) continue;
    if (polylineHitsPolygon(edgeDisplayPolyline(e, src, tgt), ring, 12)) ids.push(e.id);
  }
  return ids;
}
